/**
 * Readers Hooks
 *
 * React Query hooks for Beyond Readers books.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import * as readersService from "../services/readersService";
import type { CreateBookRequest, UpdateBookRequest } from "../types/material";

export function useBooks(year?: number) {
  return useQuery({
    queryKey: ["books", year],
    queryFn: () => readersService.getBooks(year),
    staleTime: 1000 * 60 * 5,
  });
}

export function useCreateBook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateBookRequest) => readersService.createBook(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["books"] });
      toast.success("Livro criado com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao criar livro");
    },
  });
}

export function useUpdateBook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBookRequest }) =>
      readersService.updateBook(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["books"] });
      toast.success("Livro atualizado com sucesso!");
    },
    onError: () => {
      toast.error("Erro ao atualizar livro");
    },
  });
}

export function useDeleteBook() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => readersService.deleteBook(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["books"] });
      toast.success("Livro removido!");
    },
    onError: () => {
      toast.error("Erro ao remover livro");
    },
  });
}

// Shape of the built-in default books (front-end seed in ReadersPage).
interface DefaultBook {
  month: number;
  title: string;
  author: string;
  pages: number;
  coverUrl: string;
  amazonUrl: string;
  description: string;
  tagline?: string;
  durationMonths?: number;
}

/**
 * One-time import of the built-in 2026 books into the database, so the
 * director can edit/remove them instead of recreating from scratch.
 */
export function useImportDefaultBooks() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (books: DefaultBook[]) => {
      // Idempotent: only create books for months not already in the DB.
      // Guards against double-clicks and retries after a partial failure.
      const existing = await readersService.getBooks(2026);
      const taken = new Set(existing.map((b) => b.month));

      const payloads: CreateBookRequest[] = books
        .filter((b) => !taken.has(b.month))
        .map((b) => ({
          title: b.title,
          author: b.author,
          description: b.description,
          tagline: b.tagline ?? "",
          pages: b.pages,
          coverUrl: b.coverUrl,
          amazonUrl: b.amazonUrl,
          meetLink: "",
          month: b.month,
          year: 2026,
          durationMonths: b.durationMonths ?? 1,
        }));

      await Promise.all(payloads.map((p) => readersService.createBook(p)));
      return { created: payloads.length, skipped: books.length - payloads.length };
    },
    onSuccess: ({ created, skipped }) => {
      queryClient.invalidateQueries({ queryKey: ["books"] });
      if (created === 0) {
        toast.success("Todos os livros já estavam importados");
      } else {
        toast.success(
          `${created} livro(s) importado(s)${skipped ? ` (${skipped} já existiam)` : ""}`,
        );
      }
    },
    onError: () => {
      toast.error("Erro ao importar livros");
    },
  });
}
